import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DateFnsAdapter from '@date-io/date-fns';

import { BaseAppActions } from 'state/baseapplication/Action';
import { useUpdateEffect } from 'react-use';
// import { isValidDate } from 'shared/helpers/AgeOfDate';

const dateFns = new DateFnsAdapter();

/**
 * Custom hook for managing the state of the dashboard page.
 *
 * @returns {Array} - A tuple containing the state object and the state update function.
 * @returns {State} - The state object.
 * @returns {React.Dispatch<React.SetStateAction<State>>} - The state update function.
 */
export const useDashboardState = (): [
    StateDashboard,
    React.Dispatch<React.SetStateAction<StateDashboard>>,
    any
] => {
    const dispatch = useDispatch();
    /**
     * The State is use when redux sidebar is implemented
     */
    const {
        rowsPerPage: reduxRowsPerPage,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        textField: reduxTextField,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        searchBy: reduxSearchBy,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        sortBy: reduxSortBy,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        packetStatus: reduxPacketStatus,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        packetType: reduxPacketType,
        binNumber,
        clientName,
        cm9Number,
        loanNumber,
        operatorId,
        slaDaysLeft,

        uploadAge,
        uploadDate,
        completedDate,
        reduxInitValues: {
            binNumber: reduxbinNumber,
            clientName: reduxclientName,
            cm9Number: reduxcm9Number,
            completedDate: reduxcompletedDate,
            limit: reduxlimit,
            loanNumber: reduxloanNumber,
            offset: reduxoffset,
            operatorId: reduxoperatorId,
            packetStatus: reduxpacketStatus,
            packetType: reduxpacketType,
            slaDaysLeft: reduxslaDaysLeft,
            sort: reduxsort,
            uploadAge: reduxuploadAge,
            uploadDate: reduxuploadDate,
        },
        initializeFromLogin,
        initializeFromReload,
        initializeFromRedux,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        resetInitialize,
    } = useSelector(
        (selectorState: any) =>
            selectorState.baseAppState.sidebarOptions.dashboard
    );
    const [resetState, setResetState] = useState<boolean>(false);
    const router = useSelector((selectorState: any) => selectorState.router);

    const formattedPacketType = React.useMemo(
        () =>
            reduxpacketType
                ? reduxpacketType
                      .map(
                          (item: string) =>
                              item.charAt(0).toUpperCase() + item.slice(1)
                      )
                      .join(', ')
                : '',
        [reduxpacketType, initializeFromRedux, resetState]
    );

    const formattedPacketStatus = React.useMemo(
        () =>
            reduxpacketStatus
                ? reduxpacketStatus
                      .map(
                          (item: string) =>
                              item.charAt(0).toUpperCase() + item.slice(1)
                      )
                      .join(', ')
                : '',
        [reduxpacketStatus, initializeFromRedux, resetState]
    );

    const generateSortByLabelCompletedFromReduxState = (
        sortBy: string
    ): string => {
        let sortByValue = '';
        switch (sortBy) {
            case 'dateCompleted':
                sortByValue = 'Newest First';
                break;
            case 'desDateCompleted':
                sortByValue = 'Oldest First';
                break;

            default:
                break;
        }
        return sortByValue;
    };
    const generateSortByLabelUploadFromReduxState = (
        sortBy: string
    ): string => {
        let sortByValue = '';
        switch (sortBy) {
            case 'dateOfUpload':
                sortByValue = 'Newest First';
                break;
            case 'oldestToNew':
                sortByValue = 'Oldest First';
                break;

            default:
                break;
        }
        return sortByValue;
    };
    const generateSortByLabelSLAFromReduxState = (sortBy: string): string => {
        let sortByValue = '';
        switch (sortBy) {
            case 'daysLeftToSLA':
                sortByValue = 'Newest First';
                break;
            case 'desDaysLeftToSLA':
                sortByValue = 'Oldest First';
                break;

            default:
                break;
        }
        return sortByValue;
    };

    const generateSortByLabelEscalatedFromReduxState = (
        sortBy: string
    ): string => {
        let sortByValue = '';
        switch (sortBy) {
            case 'escalatedAge':
                sortByValue = 'Newest First';
                break;
            case 'desEscalatedAge':
                sortByValue = 'Oldest First';
                break;
            default:
                break;
        }
        return sortByValue;
    };

    const [state, setState] = useState<StateDashboard>({
        data: [],
        totalRows: 0,
        rowsPerPage: reduxRowsPerPage,
        page: 0,
        isLoading: true,
        isUpdateTextField: false,
        searchBy: reduxSearchBy ?? '',
        sortBy: reduxSortBy ?? '',
        textField: reduxTextField ?? '',
        packetStatus: reduxPacketStatus ?? [],
        packetType: reduxPacketType ?? [],
        searchValues: {
            SLADate: generateSortByLabelSLAFromReduxState(reduxSortBy),
            completedDate:
                generateSortByLabelCompletedFromReduxState(reduxSortBy),
            escalatedDate:
                generateSortByLabelEscalatedFromReduxState(reduxSortBy),
            packetStatus: formattedPacketStatus,
            packetType: formattedPacketType,
            uploadedDate: generateSortByLabelUploadFromReduxState(reduxSortBy),
            limit: reduxlimit,
            offset: reduxoffset,
            sort: reduxsort ?? '',
            binNumber,
            clientName,
            cm9Number,
            loanNumber,
            operatorId,
            slaDaysLeft,

            uploadAge,
            uploadDate,
            completedDateSearch: completedDate,
        },
        searchInitialValues: {
            SLADate: generateSortByLabelSLAFromReduxState(reduxSortBy),
            completedDate:
                generateSortByLabelCompletedFromReduxState(reduxSortBy),
            escalatedDate:
                generateSortByLabelEscalatedFromReduxState(reduxSortBy),
            packetStatus: formattedPacketStatus,
            packetType: formattedPacketType,
            uploadedDate: generateSortByLabelUploadFromReduxState(reduxSortBy),
            limit: reduxlimit,
            offset: reduxoffset,
            sort: reduxsort ?? '',
            binNumber,
            clientName,
            cm9Number,
            loanNumber,
            operatorId,
            slaDaysLeft,

            uploadAge,
            uploadDate,
            completedDateSearch: completedDate,
        },
        reduxInitValues: {
            binNumber: reduxbinNumber ?? '',
            clientName: reduxclientName ?? '',
            cm9Number: reduxcm9Number ?? '',
            completedDate: reduxcompletedDate ?? '',
            limit: reduxlimit,
            loanNumber: reduxloanNumber ?? '',
            offset: reduxoffset,
            operatorId: reduxoperatorId,
            packetStatus: reduxpacketStatus ?? [],
            packetType: reduxpacketType ?? [],
            slaDaysLeft: reduxslaDaysLeft ?? '',
            sort: reduxsort ?? '',
            uploadAge: reduxuploadAge ?? '',
            uploadDate: reduxuploadDate ?? '',
        },
        initializeInDashboard: false,
        isReduxInitialize: false,
        isDrawerOpen: true,
    });

    useUpdateEffect(() => {
        if (
            Array.isArray(router.previousLocations) &&
            router.previousLocations.length === 1 &&
            initializeFromLogin === true
        ) {
            setResetState(!resetInitialize);
            if (resetInitialize) {
                setState((prevState) => ({
                    ...prevState,
                    isReduxInitialize: false,
                }));
            }
        } else if (
            Array.isArray(router.previousLocations) &&
            router.previousLocations.length > 1 &&
            initializeFromLogin === true &&
            initializeFromRedux === true
        ) {
            setResetState(!resetInitialize);
            if (resetInitialize) {
                setState((prevState) => ({
                    ...prevState,
                    isReduxInitialize: false,
                }));
            }
        }
    }, [resetInitialize]);

    useUpdateEffect(() => {
        if (resetState) {
            // console.log('reset', resetInitialize);
            setState((prevState) => ({
                ...prevState,
                isReduxInitialize: true,
                searchInitialValues: {
                    SLADate: generateSortByLabelSLAFromReduxState(reduxSortBy),
                    completedDate:
                        generateSortByLabelCompletedFromReduxState(reduxSortBy),
                    escalatedDate:
                        generateSortByLabelEscalatedFromReduxState(reduxSortBy),
                    packetStatus: formattedPacketStatus,
                    packetType: formattedPacketType,
                    uploadedDate:
                        generateSortByLabelUploadFromReduxState(reduxSortBy),
                    limit: reduxlimit,
                    offset: reduxoffset,
                    sort: reduxsort ?? '',
                    binNumber: reduxbinNumber,
                    clientName: reduxclientName,
                    cm9Number: reduxcm9Number,
                    loanNumber: reduxloanNumber,
                    operatorId: reduxoperatorId,
                    slaDaysLeft: reduxslaDaysLeft,

                    uploadAge: reduxuploadAge,
                    uploadDate: reduxuploadDate
                        ? dateFns
                              .parse(reduxuploadDate, 'yyyy-MM-dd')
                              .toISOString()
                        : '',
                    completedDateSearch: completedDate,
                },
                reduxInitValues: {
                    binNumber: reduxbinNumber,
                    clientName: reduxclientName,
                    cm9Number: reduxcm9Number,
                    completedDate: reduxcompletedDate
                        ? dateFns
                              .parse(reduxcompletedDate, 'yyyy-MM-dd')
                              .toISOString()
                        : '',
                    limit: reduxlimit,
                    loanNumber: reduxloanNumber,
                    offset: reduxoffset,
                    operatorId: reduxoperatorId,
                    packetStatus: reduxpacketStatus ?? [],
                    packetType: reduxpacketType ?? [],
                    slaDaysLeft:
                        Number.isNaN(parseInt(reduxslaDaysLeft, 10)) === false
                            ? parseInt(reduxslaDaysLeft, 10)
                            : '',
                    sort: reduxsort,
                    uploadAge:
                        Number.isNaN(parseInt(reduxuploadAge, 10)) === false
                            ? parseInt(reduxuploadAge, 10)
                            : '',
                    uploadDate: reduxuploadDate
                        ? dateFns
                              .parse(reduxuploadDate, 'yyyy-MM-dd')
                              .toISOString()
                        : '',
                },
                isDrawerOpen: true,
            }));
        }
    }, [resetState]);

    useEffect(() => {
        if (
            initializeFromRedux &&
            (initializeFromLogin === false || initializeFromReload === false)
        ) {
            // console.log('reset', resetInitialize);
            dispatch(BaseAppActions.setSidebarDashboardThemeLoggedIn(true));
            setState((prevState) => ({
                ...prevState,
                initializeInDashboard: true,
                isReduxInitialize: true,
                searchInitialValues: {
                    SLADate: generateSortByLabelSLAFromReduxState(reduxSortBy),
                    completedDate:
                        generateSortByLabelCompletedFromReduxState(reduxSortBy),
                    escalatedDate:
                        generateSortByLabelEscalatedFromReduxState(reduxSortBy),
                    packetStatus: formattedPacketStatus,
                    packetType: formattedPacketType,
                    uploadedDate:
                        generateSortByLabelUploadFromReduxState(reduxSortBy),
                    limit: reduxlimit,
                    offset: reduxoffset,
                    sort: reduxsort ?? '',
                    binNumber: reduxbinNumber,
                    clientName: reduxclientName,
                    cm9Number: reduxcm9Number,
                    loanNumber: reduxloanNumber,
                    operatorId: reduxoperatorId,
                    slaDaysLeft: reduxslaDaysLeft,

                    uploadAge: reduxuploadAge,
                    uploadDate: reduxuploadDate
                        ? dateFns
                              .parse(reduxuploadDate, 'yyyy-MM-dd')
                              .toISOString()
                        : '',
                    completedDateSearch: completedDate,
                },
                reduxInitValues: {
                    binNumber: reduxbinNumber,
                    clientName: reduxclientName,
                    cm9Number: reduxcm9Number,
                    completedDate: reduxcompletedDate
                        ? dateFns
                              .parse(reduxcompletedDate, 'yyyy-MM-dd')
                              .toISOString()
                        : '',
                    limit: reduxlimit,
                    loanNumber: reduxloanNumber,
                    offset: reduxoffset,
                    operatorId: reduxoperatorId,
                    packetStatus: reduxpacketStatus ?? [],
                    packetType: reduxpacketType ?? [],
                    slaDaysLeft:
                        Number.isNaN(parseInt(reduxslaDaysLeft, 10)) === false
                            ? parseInt(reduxslaDaysLeft, 10)
                            : '',
                    sort: reduxsort,
                    uploadAge:
                        Number.isNaN(parseInt(reduxuploadAge, 10)) === false
                            ? parseInt(reduxuploadAge, 10)
                            : '',
                    uploadDate: reduxuploadDate
                        ? dateFns
                              .parse(reduxuploadDate, 'yyyy-MM-dd')
                              .toISOString()
                        : '',
                },
                isDrawerOpen: true,
            }));
        }
    }, [initializeFromRedux]);

    const setSearchValues = (newSearchValues: Record<string, string>): void => {
        setState((prevState) => ({
            ...prevState,
            searchValues: { ...prevState.searchValues, ...newSearchValues },
        }));
    };

    return [state, setState, setSearchValues];
};
